import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import "./StudentDashboard.css";

const StudentDashboard = () => {
  const navigate = useNavigate();
  const [student, setStudent] = useState({
    name: "Alex Johnson",
    id: "STD12345",
    email: "alex.johnson@example.com",
    program: "Computer Science",
    semester: "Fall 2025",
    year: "3rd Year",
    gpa: "3.8"
  });
  
  const [courses, setCourses] = useState([
    { id: "CS301", name: "Data Structures", credits: 4, grade: "A" },
    { id: "CS302", name: "Algorithms", credits: 4, grade: "A-" },
    { id: "MATH201", name: "Linear Algebra", credits: 3, grade: "B+" },
    { id: "ENG101", name: "Technical Writing", credits: 2, grade: "A" },
  ]);
  
  const [announcements, setAnnouncements] = useState([
    {
      id: 1,
      title: "Upcoming Midterm Exams",
      date: "May 15, 2025",
      content: "Midterm exams will be held from May 15-20. Please check the schedule."
    },
    {
      id: 2,
      title: "Registration for Fall 2025",
      date: "May 10, 2025",
      content: "Registration for Fall 2025 semester begins next week."
    },
    {
      id: 3,
      title: "Campus Event: Tech Fair",
      date: "May 5, 2025",
      content: "Don't miss the annual Tech Fair happening this weekend!"
    }
  ]);
  
  const [isLoading, setIsLoading] = useState(true);

  // Simulate data loading and check authentication
  useEffect(() => {
    // In a real app, you would check if the user is authenticated here
    const checkAuth = () => {
      const studentEmail = localStorage.getItem("studentEmail");
      if (!studentEmail) {
        // If no authentication data is found, redirect to login
        navigate("/student-login");
        return false;
      }
      return true;
    };

    const fetchData = async () => {
      try {
        // Only fetch data if authenticated
        if (checkAuth()) {
          // Simulate API call delay
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // In a real app, you would fetch the actual student data here
          // For now, we're using the default data set above
          
          // Get email from localStorage to customize the experience
          const studentEmail = localStorage.getItem("studentEmail");
          if (studentEmail) {
            setStudent(prev => ({
              ...prev,
              email: studentEmail
            }));
          }
        }
      } catch (error) {
        console.error("Error fetching student data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [navigate]);
  
  // Handle logout
  const handleLogout = () => {
    // Clear any stored tokens or session data
    localStorage.removeItem("studentEmail");
    sessionStorage.clear();
    
    // Navigate back to student login page
    navigate("/student-login");
  };

  if (isLoading) {
    return (
      <div className="student-dashboard-loader min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-400 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="student-dashboard min-h-screen bg-gray-50">
      {/* Header / Navigation */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <h1 className="text-xl font-bold text-blue-600">Student Portal</h1>
              </div>
              <div className="hidden md:block">
                <div className="ml-10 flex items-baseline space-x-4">
                  <Link to="/student-dashboard" className="px-3 py-2 rounded-md text-sm font-medium text-white bg-blue-500">Dashboard</Link>
                  <Link to="/student-courses" className="px-3 py-2 rounded-md text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100">Courses</Link>
                  <Link to="/student-grades" className="px-3 py-2 rounded-md text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100">Grades</Link>
                  <Link to="/student-schedule" className="px-3 py-2 rounded-md text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100">Schedule</Link>
                </div>
              </div>
            </div>
            <div className="flex items-center">
              <span className="text-gray-600 mr-4">{student.name}</span>
              <button
                onClick={handleLogout}
                className="px-3 py-2 rounded-md text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Welcome Section */}
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Welcome, {student.name}!</h2>
            <p className="text-gray-600">
              Student ID: {student.id} | Program: {student.program} | Semester: {student.semester}
            </p>
          </div>
        </div>

        {/* Dashboard Grid */}
        <div className="px-4 sm:px-0">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Student Information Card */}
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Student Information</h3>
              <div className="space-y-3">
                <p className="flex justify-between text-sm">
                  <span className="text-gray-500">Name:</span>
                  <span className="font-medium">{student.name}</span>
                </p>
                <p className="flex justify-between text-sm">
                  <span className="text-gray-500">ID:</span>
                  <span className="font-medium">{student.id}</span>
                </p>
                <p className="flex justify-between text-sm">
                  <span className="text-gray-500">Email:</span>
                  <span className="font-medium">{student.email}</span>
                </p>
                <p className="flex justify-between text-sm">
                  <span className="text-gray-500">Program:</span>
                  <span className="font-medium">{student.program}</span>
                </p>
                <p className="flex justify-between text-sm">
                  <span className="text-gray-500">Year:</span>
                  <span className="font-medium">{student.year}</span>
                </p>
                <p className="flex justify-between text-sm">
                  <span className="text-gray-500">GPA:</span>
                  <span className="font-medium">{student.gpa}</span>
                </p>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200">
                <Link to="/student-profile" className="text-blue-500 hover:text-blue-700 text-sm font-medium">
                  View Full Profile
                </Link>
              </div>
            </div>

            {/* Current Courses Card */}
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Current Courses</h3>
              <div className="space-y-3">
                {courses.map(course => (
                  <div key={course.id} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                    <div>
                      <p className="font-medium text-gray-800">{course.name}</p>
                      <p className="text-xs text-gray-500">{course.id} • {course.credits} Credits</p>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      course.grade.startsWith('A') ? 'bg-green-100 text-green-800' : 
                      course.grade.startsWith('B') ? 'bg-blue-100 text-blue-800' : 
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {course.grade}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200">
                <Link to="/student-courses" className="text-blue-500 hover:text-blue-700 text-sm font-medium">
                  View All Courses
                </Link>
              </div>
            </div>

            {/* Announcements Card */}
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Announcements</h3>
              <div className="space-y-4">
                {announcements.map(announcement => (
                  <div key={announcement.id} className="border-b border-gray-100 pb-3 last:border-0">
                    <h4 className="font-medium text-gray-800">{announcement.title}</h4>
                    <p className="text-xs text-gray-500 mb-1">{announcement.date}</p>
                    <p className="text-sm text-gray-600">{announcement.content}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200">
                <Link to="/student-announcements" className="text-blue-500 hover:text-blue-700 text-sm font-medium">
                  View All Announcements
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Quick Links</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Link to="/student-schedule" className="flex flex-col items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-500 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-sm font-medium text-gray-700">Schedule</span>
              </Link>
              <Link to="/student-assignments" className="flex flex-col items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-500 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="text-sm font-medium text-gray-700">Assignments</span>
              </Link>
              <Link to="/student-exams" className="flex flex-col items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-500 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <span className="text-sm font-medium text-gray-700">Exams</span>
              </Link>
              <Link to="/student-resources" className="flex flex-col items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-500 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <span className="text-sm font-medium text-gray-700">Resources</span>
              </Link>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-auto">
        <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-500">© 2025 Student Portal. All rights reserved.</p>
            <div className="flex space-x-6">
              <Link to="/privacy-policy" className="text-sm text-gray-500 hover:text-gray-900">Privacy</Link>
              <Link to="/terms" className="text-sm text-gray-500 hover:text-gray-900">Terms</Link>
              <Link to="/help" className="text-sm text-gray-500 hover:text-gray-900">Help</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default StudentDashboard;